import type { ReactNode } from "react";
import { HelpHint } from "../help/links";
import { humanise } from "../lib/format";
import { Icon, type IconName } from "./Icon";

/** Placeholder rows while a table loads. */
export function Skeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="skeleton-table" role="status" aria-live="polite">
      <span className="sr-only">Loading</span>
      <div className="skeleton-row skeleton-row--head" aria-hidden="true">
        {Array.from({ length: columns }, (_, c) => <span key={c} className="skeleton" style={{ width: `${40 + ((c * 17) % 40)}%` }} />)}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="skeleton-row" aria-hidden="true">
          {Array.from({ length: columns }, (_, c) => (
            <span key={c} className="skeleton" style={{ width: `${45 + (((r + 2) * (c + 3) * 13) % 50)}%` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Placeholder lines inside a small card or list. */
export function SkeletonLines({ rows = 3 }: { rows?: number }) {
  return (
    <div className="skeleton-lines" role="status">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton-line" aria-hidden="true">
          <span className="skeleton" style={{ width: `${55 + ((i * 19) % 35)}%` }} />
          <span className="skeleton" style={{ width: `${30 + ((i * 11) % 25)}%` }} />
        </div>
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry, title = "Couldn't load this" }: { message: string; onRetry?: () => void; title?: string }) {
  return (
    <div className="state state--error" role="alert">
      <Icon name="warning" size={28} className="state__icon" />
      <div className="state__body">
        <p className="state__title">{title}</p>
        <p className="state__text">{message}</p>
      </div>
      {onRetry && (
        <button type="button" className="btn btn--outline btn--sm" onClick={onRetry}>
          <Icon name="refresh" size={18} /> Try again
        </button>
      )}
    </div>
  );
}

/** An empty list or screen. With `help` (on a screen's main list) it adds one line from the screen's guide and a link to it. */
export function EmptyState({ icon = "inbox", title, children, help = false }: { icon?: IconName; title: string; children?: ReactNode; help?: boolean }) {
  return (
    <div className="state state--empty">
      <Icon name={icon} size={28} className="state__icon" />
      <div className="state__body">
        <p className="state__title">{title}</p>
        {children && <p className="state__text">{children}</p>}
        {help && <HelpHint className="state__text helphint" />}
      </div>
    </div>
  );
}

export type ChipTone = "ink" | "accent" | "outline" | "primary" | "green" | "grey" | "primary-outline";

const TONE: Record<string, ChipTone> = {
  // bookings
  requested: "outline", confirmed: "grey", allocated: "ink", completed: "green", cancelled: "primary-outline",
  // leads
  new: "accent", contacted: "grey", quoted: "ink", won: "green", lost: "primary-outline", spam: "primary-outline",
  // quotes
  draft: "outline", sent: "ink", accepted: "green", expired: "primary-outline",
  // crew and outbox
  active: "green", inactive: "outline", pending: "grey", failed: "accent", dead: "primary",
  // shifts
  day: "outline", night: "ink", weekend: "grey", public_holiday: "primary",
};

/** Square status chip. Tone follows the value unless given. */
export function Chip({ value, tone, children }: { value?: string; tone?: ChipTone; children?: ReactNode }) {
  const t = tone ?? (value ? TONE[value] : undefined) ?? "outline";
  return <span className={`chip chip--${t}`}>{children ?? (value ? humanise(value) : null)}</span>;
}

export const toneOf = (value: string): ChipTone => TONE[value] ?? "outline";

/** Status dropdown styled as a status chip, so the current value reads at a glance. */
export function StatusSelect({ value, options, label, onChange }: { value: string; options: string[]; label: string; onChange: (v: string) => void }) {
  return (
    <select className={`status-select chip--${toneOf(value)}`} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
    </select>
  );
}

/** Back-compat alias used by older screens. */
export const Pill = ({ value }: { value: string }) => <Chip value={value} />;
